import React from 'react';
import { bool, func, string, number, element, node } from 'prop-types';
import { styleOverrideReason, deviceUtils } from '@ids/core';
import { CircleCheckIcon } from '@ids/internal-icons';
import Portal from '@ids/portal';
import IDSContext from '@ids/context';
import ZIndex from '@ids/zindex';
import Transition, { transitions } from '@ids/transition';
import {
  Wrapper,
  Message,
  Text,
  Icon,
  CustomLinkActionButton
} from './StyledToastMessage';

/**
 *
 */
class ToastMessage extends React.Component {
  static propTypes = {
    /**
     * Inner content of toast message
     */
    children: node.isRequired,
    /**
     * Set the time period for Toast Message to be visible
     */
    duration: number,
    /**
     * Whether to show the toast message
     */
    // eslint-disable-next-line react/boolean-prop-naming
    open: bool,
    /**
     * Callback for when toast message is closed
     */
    onClose: func,
    /**
     * Bool to show cross button or not
     */
    // eslint-disable-next-line react/boolean-prop-naming
    dismissible: bool,
    /**
     * Id used for automation testing
     */
    automationId: string,
    /**
     * Value to be displayed on button
     */
    actionLabel: string,
    /**
     * Bool to show an icon or not
     */
    // eslint-disable-next-line react/boolean-prop-naming
    showIcon: bool,
    /**
     * icon component
     */
    icon: element,
    /**
     * Callback for extra functionality when action is clicked
     */
    onActionClick: func,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    dismissible: true,
    duration: 5000,
    open: false,
    onClose: () => {},
    onActionClick: () => {},
    automationId: undefined,
    actionLabel: 'Close',
    showIcon: false,
    styleOverrideReason: undefined,
    icon: (
      <CircleCheckIcon focusable="false" data-automation-id="circleCheckIcon" />
    )
  };

  constructor(props) {
    super(props);
    this.toast = React.createRef();
    this.handleActionClick = this.handleActionClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  /**
   * Added lifecycle method to hnor when open is true at first mount.
   */
  componentDidMount() {
    this.showAndPositionToastMessage();
  }

  componentDidUpdate() {
    this.showAndPositionToastMessage();
  }

  /**
   * Utility method to show toastMessage and adjust position specific in case
   * of IE and Edge.
   */
  showAndPositionToastMessage() {
    if (this.props.open) {
      if (deviceUtils.isIE11() || deviceUtils.isEdge()) {
        document.body.style.position = 'fixed';
      }

      this.showToastMessage();
    }
  }

  setFocus() {
    this.toast.current.focus();
    if (deviceUtils.isIE11() || deviceUtils.isEdge()) {
      document.body.style.position = 'static';
    }
  }

  /**
   * handle the onclick for dismiss button
   */
  handleActionClick() {
    this.props.onActionClick();
    this.clearTimer();
  }

  clearTimer() {
    // clear the timeout
    if (typeof this.timeoutID === 'number') {
      window.clearTimeout(this.timeoutID);
      this.timeoutID = undefined;
    }
  }

  /**
   * handle the onclose when the user hits escape or
   * the toast message closes because of duration
   */
  closeToastMessage() {
    this.props.onClose();
    this.clearTimer();
  }

  /**
   * shows toast message
   */
  showToastMessage() {
    const { duration } = this.props;
    this.setFocus();
    if (duration > 1) {
      this.showDefinite();
    }
  }

  /**
   * show toast message for the given duration
   */
  showDefinite() {
    this.timeoutID = setTimeout(() => {
      this.closeToastMessage();
    }, this.props.duration);
  }

  // handle keyboard events
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeToastMessage();
    }
  }

  renderCloseButton() {
    const { actionLabel, dismissible } = this.props;
    return (
      dismissible && (
        <CustomLinkActionButton size="small" onClick={this.handleActionClick}>
          {actionLabel}
        </CustomLinkActionButton>
      )
    );
  }

  renderIcon() {
    const { icon, showIcon } = this.props;
    return showIcon && <Icon>{icon}</Icon>;
  }

  render() {
    const { children, onActionClick, open, ...restProps } = this.props;

    return (
      <Portal>
        <IDSContext.Consumer>
          {context => (
            <ZIndex
              assignZIndex={open}
              getCurrentBaseZIndex={context.getCurrentBaseZIndex}
              incrementBaseZIndex={context.incrementBaseZIndex}
              decrementBaseZIndex={context.decrementBaseZIndex}
            >
              <Transition
                unmountOnExit
                transition={open ? transitions.enter : transitions.exit}
              >
                <Wrapper
                  ref={this.toast}
                  tabIndex="-1"
                  role="status"
                  open={open}
                  onKeyDown={this.handleKeyDown}
                  {...restProps}
                >
                  <Message>
                    {this.renderIcon()}
                    <Text>{children}</Text>
                    {this.renderCloseButton()}
                  </Message>
                </Wrapper>
              </Transition>
            </ZIndex>
          )}
        </IDSContext.Consumer>
      </Portal>
    );
  }
}

ToastMessage.displayName = 'ToastMessage';

export default ToastMessage;
